import React, { useState, useEffect, lazy, Suspense, useMemo } from "react";
import { Tabs, Tab, Box, Chip } from "@mui/material";

import { ReportedPeriod } from "../../interfaces/DTO/agent/mimshakGroupBy";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { Product } from "../../interfaces/DTO/mislaka_xml_models";
import AllPolicyTables from "../PersonFinancialData/Tabels/AllPolicyTables";
import MimshakManagementPage from "../PersonFinancialData/Tabels/MimshakManagementPage";
import { usePolicyInsightsByContactIdsAndPeriod } from "../../hooks/insights/usePolicyInsightsRuleBased";
import ContactInvestmentMimshakDataTable from "../Investment/ContactInvestmentMimshakDataTable";
import { useManualMimshaksByContactsAndPeriod } from "../../hooks/useManualMimshaksData";
import { LOCAL_STORAGE_KEYS } from "../../services/local_storage_keys";

const PolicyRuleBasedInsightsForContactPage = lazy(() => import("../Insights/PolicyRuleBasedInsightsForContactPage"));
const ContactFilesPanel = lazy(() => import("../CRM/contacts/ContactFilesPanel"));
const ContactTasksTable = lazy(() => import("../UserTask/Tables/ContactTasksTable"));
const FormsPreparationHelper = lazy(() => import("../UserTask/FormsPreparation/FormsPreparationHelper"));
const DailyReportActionsViewForContacts = lazy(() => import("../UserTask/DailyReport/DailyReportActionsViewForContacts"));

export interface MainPageProps {
  data: MimshakData[];
  selectedPeriod: string;
  viewedType: "USER" | "FAMILY";
  selectedPeriodType: "REGULAR" | "PRE_ADVICE";
  periods: ReportedPeriod[];
  products: Product[];
  contacts: Contact[];
  pensionMimshakData?: MimshakData[];
  capitalMimshakData?: MimshakData[];
  insuranceMimshakData?: MimshakData[];
}

const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div>טוען...</div>}>{children}</Suspense>
);

const MainPage: React.FC<MainPageProps> = ({
  data,
  selectedPeriod,
  viewedType,
  selectedPeriodType,
  periods,
  products,
  contacts,
  pensionMimshakData = [],
  capitalMimshakData = [],
  insuranceMimshakData = [],
}) => {
  const TABS_COUNT = 5;
  const storedTab = Number(localStorage.getItem(LOCAL_STORAGE_KEYS.consultingMainPageTabsSelectedIndex)) || 0;
  const initialTab = storedTab < TABS_COUNT ? storedTab : 0;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loadedTabs, setLoadedTabs] = useState<Set<number>>(new Set([initialTab]));

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.consultingMainPageTabsSelectedIndex, activeTab.toString());
  }, [activeTab]);

  const id = contacts?.[0]?.id;

  // טאב תובנות הוא אינדקס 2
  const isInsightsTabLoaded = loadedTabs.has(2);

  // שליפת contactIds עבור כל אנשי הקשר
  const contactIds = useMemo(() => contacts.map((c) => c.id), [contacts]);

  const { data: insightsData = [], isLoading: insightsLoading } =
    usePolicyInsightsByContactIdsAndPeriod(
      contactIds,
      selectedPeriod,
      Boolean(contactIds.length > 0 && selectedPeriod && isInsightsTabLoaded)
    );

  const insightsCount = insightsData.length;

  // שליפת תיקי השקעות מ-manual mimshak data
  const { data: manualMimshakData = [] } = useManualMimshaksByContactsAndPeriod(
    contactIds,
    selectedPeriod,
    Boolean(contactIds.length > 0 && selectedPeriod)
  );

  // סינון רק תיקי השקעות (INVESTMENT_PORTFOLIO = 600)
  const investmentPortfolios = useMemo(() => {
    return manualMimshakData.filter((item) => item.product_type === 600);
  }, [manualMimshakData]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setLoadedTabs((prev) => new Set(prev).add(newValue));
  };

  const tabs = [
    {
      label: "טבלאות",
      content: (
        <>
        <AllPolicyTables
          data={data}
          viewedType={viewedType}
          selectedPeriodType={selectedPeriodType}
          contacts={contacts}
          reportedPeriod={selectedPeriod}
        />

        {/* טבלת תיקי השקעות */}
        {investmentPortfolios.length > 0 && (
          <ContactInvestmentMimshakDataTable
            data={investmentPortfolios}
            viewedType={viewedType}
          />
        )}
        </>
      ),
    },
    {
      label: "ניהול",
      content: (
        <MimshakManagementPage
          data={data}
          viewedType={viewedType}
          selectedPeriodType={selectedPeriodType}
          pensionMimshakData={pensionMimshakData}
          capitalMimshakData={capitalMimshakData}
          insuranceMimshakData={insuranceMimshakData}
          investmentMimshakData={investmentPortfolios}
          contactIds={contactIds}
          selectedPeriod={selectedPeriod}
        />
      ),
    },
    {
    label: (
      <Box display="flex" alignItems="center" gap={1}>
        תובנות
        {!insightsLoading && insightsCount > 0 && (
          <Chip
            label={insightsCount}
            size="small"
            sx={{
              backgroundColor: "orange",
              color: "white",
              borderRadius: "50%",
              height: 24,
              minWidth: 24,
              fontWeight: 500,
            }}
          />
        )}
      </Box>
    ),
    content: (
     <LazyWrapper>
      <PolicyRuleBasedInsightsForContactPage
        contactIds={contactIds}
        selectedPeriod={selectedPeriod}
        selectedPeriodType={selectedPeriodType}
        mimshakData={data}
        periods={periods}
        products={products}
        primaryUserId={id}
      />
  </LazyWrapper>
    ),
  },
    {
      label: "משימות",
      content: (
         <>
         <LazyWrapper>
          <FormsPreparationHelper contactIds={contactIds} />
          {contacts.map((contact) => (
            <Box key={`tasks-${contact.id}`} mb={4}>
              <ContactTasksTable contact={contact} reportedPeriod={selectedPeriod} />
            </Box>
          ))}
          <DailyReportActionsViewForContacts contactIds={contactIds} />
          </LazyWrapper>
        </>
       
      ),
    },
    {
      label: "קבצים",
      content: (
        <LazyWrapper>
          {contacts.map((contact) => (
            <Box key={`files-${contact.id}`} mb={4}>
              <ContactFilesPanel contactId={contact.id} contact={contact} />
            </Box>
          ))}
        </LazyWrapper>
      ),
    },
  ];

  return (
    <Box >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="secondary"
        textColor="secondary"
        sx={{ mb: 2,}}
      >
        {tabs.map((tab, index) => (
          <Tab key={index} label={tab.label} sx={{borderLeft: activeTab === index ? 'lightgray' : '1px solid #EEE', fontWeight: activeTab === index ? 'normal' : 'normal' , fontSize:'1.25rem'}} />
        ))}
      </Tabs>

      <Box sx={{  overflowY: "visible" }}>
        {loadedTabs.has(activeTab) && tabs[activeTab].content}
      </Box>
    </Box>
  );
};

export default MainPage;